'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useCommerce } from '@/context/CommerceContext';
import { CheckCircle2 } from 'lucide-react';

export default function Footer() {
  const {
    setIsAccountOpen,
    setIsTrackingOpen,
    setIsContactOpen,
    setIsRfqOpen,
  } = useCommerce();

  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      setErrorMessage('Please enter a valid email address');
      return;
    }
    setErrorMessage('');
    setIsSubscribed(true);
    setEmail('');
    setTimeout(() => {
      setIsSubscribed(false);
    }, 4500);
  };

  return (
    <footer className="bg-[#0F382A] text-white border-t border-[#0F382A] select-none">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32 lg:pt-36 pb-16 sm:pb-20 lg:pb-24">
        
        {/* ========================================================================= */}
        {/* TOP SECTION: Brand Description (Left) & Subscribe Box (Right)             */}
        {/* ========================================================================= */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start pb-20 sm:pb-24 lg:pb-28 border-b border-white/10">
          
          {/* LEFT COLUMN: Brand Logo & Description (cols 1 to 7) */}
          <div className="lg:col-span-7 max-w-2xl space-y-6 sm:space-y-7">
            <Link href="/" className="inline-flex items-center space-x-3.5 group">
              <div className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white p-1.5 border border-white/20 shadow-xs shrink-0 flex items-center justify-center transition-transform group-hover:scale-105">
                <Image
                  src="/logo-web.png"
                  alt="Elavarasi Foods"
                  width={48}
                  height={48}
                  unoptimized
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex items-center space-x-2">
                <span className="font-display font-black text-2xl sm:text-3xl tracking-tight text-white">
                  Elavarasi
                </span>
                <span className="font-display font-black text-2xl sm:text-3xl tracking-tight text-[#F7BF54]">
                  Foods
                </span>
              </div>
            </Link>

            <p className="text-base sm:text-lg text-emerald-100/80 leading-relaxed font-sans font-normal max-w-xl">
              Elavarasi Foods is the authentic South Indian heritage platform that turns traditional kitchen recipes into royal festive delicacies, handcrafted with pure cold-pressed oils and generational care.
            </p>
          </div>

          {/* RIGHT COLUMN: Subscribe Box & Privacy Note (cols 8 to 12) */}
          <div className="lg:col-span-5 w-full max-w-md lg:ml-auto space-y-3">
            <h3 className="text-lg sm:text-xl font-bold text-white tracking-tight">
              Subscribe
            </h3>

            {isSubscribed ? (
              <div className="p-4 bg-emerald-900/60 border border-emerald-400/40 rounded-2xl flex items-center space-x-3 text-emerald-100 text-sm font-medium animate-in fade-in duration-200">
                <CheckCircle2 className="w-5 h-5 text-emerald-300 shrink-0" />
                <span>Thank you for subscribing! Check your inbox for 10% off your first festive box.</span>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="space-y-2.5">
                <div className="flex items-center bg-white/10 backdrop-blur-xs rounded-2xl p-2 border border-white/15 focus-within:border-emerald-400 focus-within:bg-white/15 focus-within:ring-2 focus-within:ring-white/10 transition-all shadow-xs">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (errorMessage) setErrorMessage('');
                    }}
                    placeholder="Enter Your Email"
                    className="flex-1 bg-transparent px-4 py-2.5 text-sm sm:text-base text-white placeholder-emerald-200/50 focus:outline-none min-w-0"
                    aria-label="Email address for subscription"
                  />
                  <button
                    type="submit"
                    className="bg-white hover:bg-emerald-50 text-[#0F382A] text-xs sm:text-sm font-bold px-6 sm:px-7 py-3 rounded-xl transition-all cursor-pointer shadow-xs active:scale-95 shrink-0"
                  >
                    Get Started
                  </button>
                </div>
                {errorMessage && (
                  <p className="text-xs text-rose-300 font-medium pl-2">{errorMessage}</p>
                )}
              </form>
            )}

            <p className="text-xs sm:text-sm text-emerald-100/60 font-sans pt-1">
              By subscribing you agree to with our{' '}
              <Link href="/privacy" className="text-white hover:underline font-medium">
                Privacy Policy
              </Link>
            </p>
          </div>

        </div>

        {/* ========================================================================= */}
        {/* MIDDLE SECTION: 6-Column Navigation Grid with Generous Spacing            */}
        {/* ========================================================================= */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-10 sm:gap-12 lg:gap-8 xl:gap-12 py-20 sm:py-24 lg:py-28 border-b border-white/10">
          
          {/* Column 1: Company */}
          <div className="space-y-5">
            <h4 className="text-base font-bold text-white tracking-tight">
              Company
            </h4>
            <ul className="space-y-3.5 sm:space-y-4 text-sm sm:text-[15px] text-emerald-100/70 font-sans">
              <li>
                <Link href="/about" className="hover:text-white transition-colors inline-block py-0.5">
                  About Us
                </Link>
              </li>
              <li>
                <button
                  onClick={() => setIsContactOpen(true)}
                  className="hover:text-white transition-colors text-left cursor-pointer inline-block py-0.5"
                >
                  Contact Us
                </button>
              </li>
              <li>
                <Link href="/about" className="hover:text-white transition-colors inline-block py-0.5">
                  Blog
                </Link>
              </li>
              <li>
                <Link href="/shop" className="hover:text-white transition-colors inline-block py-0.5">
                  Pricing
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 2: Resource */}
          <div className="space-y-5">
            <h4 className="text-base font-bold text-white tracking-tight">
              Resource
            </h4>
            <ul className="space-y-3.5 sm:space-y-4 text-sm sm:text-[15px] text-emerald-100/70 font-sans">
              <li>
                <Link href="/shop" className="hover:text-white transition-colors inline-block py-0.5">
                  Library
                </Link>
              </li>
              <li>
                <Link href="/about" className="hover:text-white transition-colors inline-block py-0.5">
                  Articles
                </Link>
              </li>
              <li>
                <Link href="/festivals" className="hover:text-white transition-colors inline-block py-0.5">
                  Templates
                </Link>
              </li>
              <li>
                <Link href="/occasions" className="hover:text-white transition-colors inline-block py-0.5">
                  Events
                </Link>
              </li>
              <li>
                <button
                  onClick={() => setIsContactOpen(true)}
                  className="hover:text-white transition-colors text-left cursor-pointer inline-block py-0.5"
                >
                  Community
                </button>
              </li>
            </ul>
          </div>

          {/* Column 3: Industry */}
          <div className="space-y-5">
            <h4 className="text-base font-bold text-white tracking-tight">
              Industry
            </h4>
            <ul className="space-y-3.5 sm:space-y-4 text-sm sm:text-[15px] text-emerald-100/70 font-sans">
              <li>
                <Link href="/bulk-orders" className="hover:text-white transition-colors inline-block py-0.5">
                  Agencies
                </Link>
              </li>
              <li>
                <Link href="/corporate" className="hover:text-white transition-colors inline-block py-0.5">
                  Professional Services
                </Link>
              </li>
              <li>
                <Link href="/export" className="hover:text-white transition-colors inline-block py-0.5">
                  Technology
                </Link>
              </li>
              <li>
                <Link href="/occasions" className="hover:text-white transition-colors inline-block py-0.5">
                  Life Sciences
                </Link>
              </li>
              <li>
                <Link href="/bulk-orders" className="hover:text-white transition-colors inline-block py-0.5">
                  Insurance
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Role */}
          <div className="space-y-5">
            <h4 className="text-base font-bold text-white tracking-tight">
              Role
            </h4>
            <ul className="space-y-3.5 sm:space-y-4 text-sm sm:text-[15px] text-emerald-100/70 font-sans">
              <li>
                <Link href="/corporate" className="hover:text-white transition-colors inline-block py-0.5">
                  Chief Executive Officers
                </Link>
              </li>
              <li>
                <Link href="/corporate" className="hover:text-white transition-colors inline-block py-0.5">
                  Human Resources Leaders
                </Link>
              </li>
              <li>
                <Link href="/bulk-orders" className="hover:text-white transition-colors inline-block py-0.5">
                  People Managers
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 5: Support */}
          <div className="space-y-5">
            <h4 className="text-base font-bold text-white tracking-tight">
              Support
            </h4>
            <ul className="space-y-3.5 sm:space-y-4 text-sm sm:text-[15px] text-emerald-100/70 font-sans">
              <li>
                <button
                  onClick={() => setIsContactOpen(true)}
                  className="hover:text-white transition-colors text-left cursor-pointer inline-block py-0.5"
                >
                  Help Center
                </button>
              </li>
              <li>
                <button
                  onClick={() => setIsTrackingOpen(true)}
                  className="hover:text-white transition-colors text-left cursor-pointer inline-block py-0.5"
                >
                  System Status
                </button>
              </li>
              <li>
                <a
                  href="https://wa.me/919443210890"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors inline-block py-0.5"
                >
                  Community
                </a>
              </li>
              <li>
                <Link href="/shop" className="hover:text-white transition-colors inline-block py-0.5">
                  Sitemap
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 6: Social Media */}
          <div className="space-y-5">
            <h4 className="text-base font-bold text-white tracking-tight">
              Social Media
            </h4>
            <ul className="space-y-3.5 sm:space-y-4 text-sm sm:text-[15px] text-emerald-100/70 font-sans">
              <li>
                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors block py-0.5"
                >
                  Twitter
                </a>
              </li>
              <li>
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors block py-0.5"
                >
                  LinkedIn
                </a>
              </li>
              <li>
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors block py-0.5"
                >
                  Instagram
                </a>
              </li>
              <li>
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors block py-0.5"
                >
                  Facebook
                </a>
              </li>
              <li>
                <a
                  href="https://youtube.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors block py-0.5"
                >
                  YouTube
                </a>
              </li>
            </ul>
          </div>

        </div>

        {/* ========================================================================= */}
        {/* BOTTOM BAR: Privacy Policy, Terms of Use (Left) & Copyright (Right)       */}
        {/* ========================================================================= */}
        <div className="pt-12 sm:pt-16 pb-4 sm:pb-8 flex flex-col sm:flex-row items-center justify-between gap-6 text-sm text-emerald-100/60 font-sans">
          <div className="flex items-center space-x-8 sm:space-x-10">
            <Link href="/privacy" className="hover:text-white transition-colors">
              Privacy policy
            </Link>
            <Link href="/terms" className="hover:text-white transition-colors">
              Terms of Use
            </Link>
          </div>

          <p className="text-emerald-100/60">
            © 2026 All rights reserved
          </p>
        </div>

      </div>
    </footer>
  );
}
